import React from 'react';
import PropTypes from 'prop-types';
import {
  Page,
  View,
  Text,
} from '@react-pdf/renderer';

import FlagIcon from '../../assets/icons/FlagIcon';

import TablePageHeader from '../../components/TablePageHeader';
import BottomPagination from '../../components/BottomPagination';
import DraftOverlay from '../../components/DraftOverlay';

export default function MarketEquityAdjustment({ reportData, totalPageCount, exhibitNumber }) {
  const tableData = reportData.MarketEquityAdjustment;
  const footnote = '';
  const selectedME = tableData.selectedMarketEquityAdjustment;
  const table1Headers = tableData.tableHeaders;
  const summaryLabels = [
    'Maximum',
    '95th percentile',
    '90th percentile',
    '75th percentile',
    'Median',
    '25th percentile',
    '10th percentile',
    '5th percentile',
    'Minimum',
    'Average',
  ];
  const statToIndexMap = {
    'Maximum': 0,
    '95th percentile': 1,
    '90th percentile': 2,
    '75th percentile': 3,
    'Median': 5,
    '25th percentile': 4,
    '10th percentile': 7,
    '5th percentile': 8,
    'Minimum': 9,
    'Average': 10,
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === '' || value === '-') return null;
    const parsed = parseFloat(String(value).replace(/[$,%]/g, '').replaceAll(',', ''));
    return Number.isNaN(parsed) ? null : parsed;
  };

  const quantile = (sortedAsc, q) => {
    if (!sortedAsc.length) return null;
    if (sortedAsc.length === 1) return sortedAsc[0];
    const position = (sortedAsc.length - 1) * q;
    const base = Math.floor(position);
    const rest = position - base;
    const next = sortedAsc[base + 1] ?? sortedAsc[base];
    return sortedAsc[base] + rest * (next - sortedAsc[base]);
  };

  const getSummaryStatistic = (values, rowName) => {
    if (!values.length) return null;
    const sortedAsc = [...values].sort((a, b) => a - b);
    if (rowName === 'Maximum') return sortedAsc[sortedAsc.length - 1];
    if (rowName === 'Minimum') return sortedAsc[0];
    if (rowName === 'Average') return values.reduce((sum, value) => sum + value, 0) / values.length;
    if (rowName === 'Median') return quantile(sortedAsc, 0.5);
    if (rowName === '95th percentile') return quantile(sortedAsc, 0.95);
    if (rowName === '90th percentile') return quantile(sortedAsc, 0.90);
    if (rowName === '75th percentile') return quantile(sortedAsc, 0.75);
    if (rowName === '25th percentile') return quantile(sortedAsc, 0.25);
    if (rowName === '10th percentile') return quantile(sortedAsc, 0.10);
    if (rowName === '5th percentile') return quantile(sortedAsc, 0.05);
    return null;
  };

  const rowIndexesForSummary = tableData.Name
    .map((_, index) => index)
    .filter((index) => !tableData.Outlier || tableData.Outlier[index] !== 1);

  const summarySeries = {
    TD: rowIndexesForSummary
      .map((index) => parseNumber(tableData.marketCapTD?.[index]))
      .filter((value) => value !== null),
    VD: rowIndexesForSummary
      .map((index) => parseNumber(tableData.marketCapVD?.[index]))
      .filter((value) => value !== null),
    GROWTH: rowIndexesForSummary
      .map((index) => parseNumber(tableData.marketGrowthTDtoVD?.[index]))
      .filter((value) => value !== null),
  };

  const formatSummaryValue = (dataName, numericValue) => {
    if (numericValue === null || numericValue === undefined) return '-';
    if (dataName === 'Market Growth') {
      const percentValue = Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;
      return `${Math.round(percentValue)}%`;
    }
    return `$${Math.round(numericValue).toLocaleString('en-US')}`;
  };

  function returnCellData(dataName, rowIndex, pageIndex) {
    if (dataName === 'Company name') return tableData.Name[rowIndex + (pageIndex * 37)];
    if (dataName === 'Ticker') return tableData.Ticker[rowIndex + (pageIndex * 37)];
    let valToReturn = null;
    if (dataName.includes('TD')) {
      valToReturn = tableData.marketCapTD[rowIndex + (pageIndex * 37)];
    }
    if (dataName.includes('VD')) {
      valToReturn = tableData.marketCapVD[rowIndex + (pageIndex * 37)];
    }
    if (dataName === 'Market Growth') {
      valToReturn = tableData.marketGrowthTDtoVD[rowIndex + (pageIndex * 37)];
    }
    return valToReturn || '-';
  }

  function returnSummaryData(dataName, rowIndex) {
    const statName = summaryLabels[rowIndex];
    let seriesKey = null;
    if (dataName.includes('TD')) {
      seriesKey = 'TD';
    }
    if (dataName.includes('VD')) {
      seriesKey = 'VD';
    }
    if (dataName === 'Market Growth') {
      seriesKey = 'GROWTH';
    }

    const computedValue = getSummaryStatistic(summarySeries[seriesKey] || [], statName);
    if (computedValue !== null) return formatSummaryValue(dataName, computedValue);
    return '-';
  }

  const numberOfPages = Math.floor((reportData.ValuationDateVolatility.volatilityObject.Name.length + 10) / 37) || 1;

  return (
    <>
      {[...Array(numberOfPages)].map((_, i) => i).map((pageNumber, pageIndex) => (
        <Page
          key={pageNumber}
          size="TABLOID"
          orientation="landscape"
          style={{ position: 'relative', backgroundColor: '#FAFAFD' }}
        >
          <TablePageHeader
            companyName={reportData.companyInfo.companyName}
            pageName={`Market Equity Adjustment, Industry   |   Exhibit ${exhibitNumber}`}
          />
          <View
            style={{
              marginTop: '84px',
              marginLeft: '32px',
              display: 'flex',
              flexDirection: 'column',
              width: '1160px',
              border: '1px',
              borderColor: '#AFB1B3',
            }}
          >
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: '24px',
                backgroundColor: '#303D5C',
              }}
            >
              <Text
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#8AB9A4',
                  paddingLeft: '16px',
                  letterSpacing: '0.08px',
                  width: '60px',
                  paddingTop: '6px',
                  borderBottom: '1px',
                  borderColor: '#CCCCCC',
                  height: '24px',
                }}
              >
                (USD$)
              </Text>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: '24px',
                  width: '385px',
                  marginRight: '8px',
                  paddingLeft: '6px',
                  borderBottom: '1px',
                  borderColor: '#CCCCCC',
                }}
              >
                <Text
                  style={{
                    fontFamily: 'RobotoMed',
                    fontSize: '10px',
                    color: '#8AB9A4',
                    letterSpacing: '0.08px',
                    paddingTop: '6px',
                    height: '24px',
                  }}
                >
                  (
                </Text>
                <View
                  style={{
                    height: '16px',
                    width: '12px',
                    paddingTop: '1px',
                  }}
                >
                  <FlagIcon fillColor="#98B8AA" />
                </View>
                <Text
                  style={{
                    fontFamily: 'RobotoMed',
                    fontSize: '10px',
                    color: '#8AB9A4',
                    letterSpacing: '0.08px',
                    marginRight: '8px',
                    paddingTop: '6px',
                    height: '24px',
                  }}
                >
                  = Outlier)
                </Text>
              </View>
              <Text
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#E1E2EC',
                  paddingLeft: '16px',
                  letterSpacing: '0.08px',
                  width: '475px',
                  paddingTop: '6px',
                  borderBottom: '1px',
                  borderColor: '#CCCCCC',
                  height: '24px',
                  textAlign: 'center',
                }}
              >
                Market capitalization
              </Text>
              <View
                style={{
                  height: '24px',
                  width: '240px',
                  marginLeft: '8px',
                  paddingLeft: '6px',
                  borderBottom: '1px',
                  borderColor: '#CCCCCC',
                }}
              />
            </View>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                height: '40px',
                backgroundColor: '#575E71',
                borderBottom: '1px',
                borderColor: '#CCCCCC',
              }}
            >
              {table1Headers.map((dataName, index) => (
                <View key={dataName}>
                  {dataName === 'Outlier' ? (
                    <View
                      style={{
                        height: '16px',
                        width: '24px',
                        paddingLeft: '10px',
                        marginTop: 'auto',
                      }}
                    >
                      <FlagIcon fillColor="#98B8AA" />
                    </View>
                  ) : (
                    <Text
                      key={dataName}
                      style={{
                        fontFamily: 'RobotoMed',
                        fontSize: '10px',
                        color: '#E1E2EC',
                        width: index === 0 ? '20px' : index === 1 ? '200px' : '235px',
                        marginTop: 'auto',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        letterSpacing: '0.5px',
                        textAlign: index > 1 && 'right',
                        lineHeight: '0.55mm',
                      }}
                    >
                      {dataName.replace(/VD|TD/g, '')}
                    </Text>
                  )}
                </View>
              ))}
            </View>
            {tableData.Name.slice(pageIndex * 37, (pageIndex + 1) * 37).map((compName, rowIndex) => (
              <View
                key={compName}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: '16px',
                  backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                }}
              >
                {table1Headers.map((dataName, cellIndex) => (
                  <View key={dataName}>
                    {cellIndex === 0 ? (
                      <View
                        style={{
                          height: '16px',
                          width: '24px',
                          paddingLeft: '10px',
                          marginTop: '2px',
                        }}
                      >
                        {tableData.Outlier[rowIndex + (pageIndex * 37)] === 1 && (
                          <FlagIcon fillColor="#307157" />
                        )}
                      </View>
                    ) : (
                      <Text
                        key={dataName}
                        style={{
                          fontFamily: 'Roboto',
                          fontSize: '10px',
                          color: '#49454F',
                          width: cellIndex === 0 ? '20px' : cellIndex === 1 ? '200px' : '235px',
                          paddingLeft: '16px',
                          paddingRight: '16px',
                          letterSpacing: '0.4px',
                          textAlign: cellIndex > 1 && 'right',
                        }}
                      >
                        {returnCellData(dataName, rowIndex, pageIndex) || 'N/A'}
                      </Text>
                    )}
                  </View>
                ))}
              </View>
            ))}
          </View>
          {(pageIndex === numberOfPages - 1) && (tableData.Name.slice(pageIndex * 37, (pageIndex + 1) * 37).length <= 24) && (
            <>
              <View
                style={{
                  marginTop: '16px',
                  marginLeft: '32px',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '1160px',
                  border: '1px',
                  borderColor: '#AFB1B3',
                }}
              >
                {summaryLabels.map((rowName, rowIndex) => (
                  <View
                    key={rowName}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      height: '16px',
                      backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                    }}
                  >
                    {table1Headers.map((dataName, cellIndex) => (
                      <Text
                        key={dataName}
                        style={{
                          fontFamily: cellIndex === 0 ? 'RobotoMed' : 'Roboto',
                          fontSize: '10px',
                          color: '#49454F',
                          width: cellIndex === 0 ? '200px' : '245px',
                          display: cellIndex === 1 ? 'none' : 'block',
                          paddingLeft: '16px',
                          paddingRight: '16px',
                          letterSpacing: '0.4px',
                          textAlign: cellIndex !== 0 && 'right',
                        }}
                      >
                        {cellIndex === 0 ? rowName : returnSummaryData(dataName, rowIndex)}
                      </Text>
                    ))}
                  </View>
                ))}
              </View>
              <View
                style={{
                  width: '1160px',
                  marginTop: '16px',
                  marginLeft: '32px',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  height: '24px',
                  backgroundColor: '#b8c6e0',
                }}
              >
                <Text
                  style={{
                    fontFamily: 'RobotoMed',
                    fontSize: '14px',
                    color: '#49454F',
                    paddingRight: '16px',
                    letterSpacing: '0.08px',
                  }}
                >
                  Selected Market Equity Adjustment:&nbsp;
                  <Text
                    style={{
                      fontFamily: 'RobotoBold',
                      fontSize: '16px',
                      color: '#000000',
                      letterSpacing: '0.1px',
                    }}
                  >
                    {selectedME}
                    %
                  </Text>
                  <Text
                    style={{
                      color: '#000',
                      fontFamily: 'RobotoBold',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: '600',
                      lineHeight: '20px',
                      letterSpacing: '0.075px',
                    }}
                  >
                    :
                    {selectedME}
                  </Text>
                </Text>
              </View>
            </>
          )}
          <BottomPagination
            pageNumber={totalPageCount + pageIndex}
            reportData={reportData}
          />
          {reportData.isDraft && <DraftOverlay />}
        </Page>
      ))}
      {tableData.Name.slice((numberOfPages - 1) * 37, numberOfPages * 37).length > 24 && (
        <Page
          size="TABLOID"
          orientation="landscape"
          style={{ position: 'relative', backgroundColor: '#FAFAFD' }}
        >
          <TablePageHeader
            companyName={reportData.companyInfo.companyName}
            pageName={`Market Equity Adjustment, Industry   |   Exhibit ${exhibitNumber}`}
          />
          <View
            style={{
              marginTop: '84px',
              marginLeft: '32px',
              display: 'flex',
              flexDirection: 'column',
              width: '1160px',
              border: '1px',
              borderColor: '#AFB1B3',
            }}
          >
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                height: '40px',
                backgroundColor: '#575E71',
                borderBottom: '1px',
                borderColor: '#CCCCCC',
              }}
            >
              {table1Headers.map((dataName, index) => (
                <Text
                  key={dataName}
                  style={{
                    display: 'flex',
                    fontFamily: 'RobotoMed',
                    fontSize: '10px',
                    color: '#E1E2EC',
                    width: index > 2 ? '240px' : '0px',
                    marginTop: 'auto',
                    paddingLeft: index > 2 ? '16px' : '0px',
                    paddingRight: index > 2 ? '16px' : '0px',
                    letterSpacing: '0.5px',
                    textAlign: index > 2 && 'right',
                    lineHeight: '0.55mm',
                  }}
                >
                  {index > 2 && dataName.replace(/VD|TD/g, '')}
                </Text>
              ))}
            </View>
            {summaryLabels.map((rowName, rowIndex) => (
              <View
                key={rowName}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: '16px',
                  backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                }}
              >
                {table1Headers.map((dataName, cellIndex) => (
                  <Text
                    key={dataName}
                    style={{
                      fontFamily: cellIndex === 0 ? 'RobotoMed' : 'Roboto',
                      fontSize: '10px',
                      color: '#49454F',
                      width: cellIndex === 0 ? '200px' : '245px',
                      display: cellIndex === 1 ? 'none' : 'block',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      letterSpacing: '0.4px',
                      textAlign: cellIndex !== 0 && 'right',
                    }}
                  >
                    {cellIndex === 0 ? rowName : cellIndex === 1 ? '' : cellIndex === 2 ? '' : returnSummaryData(dataName, rowIndex)}
                  </Text>
                ))}
              </View>
            ))}
          </View>
          <View
            style={{
              width: '1160px',
              marginTop: '16px',
              marginLeft: '32px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
              height: '24px',
              backgroundColor: '#b8c6e0',
            }}
          >
            <Text
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '14px',
                color: '#49454F',
                paddingRight: '16px',
                letterSpacing: '0.08px',
              }}
            >
              Selected Market Equity Adjustment:&nbsp;
              <Text
                style={{
                  fontFamily: 'RobotoBold',
                  fontSize: '16px',
                  color: '#000000',
                  letterSpacing: '0.1px',
                }}
              >
                {selectedME}
                %
              </Text>
            </Text>
          </View>
          {!!footnote && (
            <Text
              style={{
                fontFamily: 'Roboto',
                fontSize: '8px',
                color: '#49454F',
                letterSpacing: '0.4px',
                marginTop: '16px',
                marginLeft: '90px',
                width: '450px',
                lineHeight: '0.55mm',
              }}
            >
              Footnote:&nbsp;
              {footnote}
            </Text>
          )}
          <BottomPagination
            pageNumber={totalPageCount + numberOfPages}
            reportData={reportData}
          />
          {reportData.isDraft && <DraftOverlay />}
        </Page>
      )}
    </>
  );
}

MarketEquityAdjustment.propTypes = {
  reportData: PropTypes.object.isRequired,
  totalPageCount: PropTypes.number.isRequired,
  exhibitNumber: PropTypes.number.isRequired,
};
